import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Lock, Mail, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../components/AppProvider';
import { initRecaptcha, executeRecaptcha, verifyRecaptcha, unloadRecaptcha, isDevEnvironment } from '../../lib/recaptcha';
import { sessionChecker } from '../../lib/utils/sessionChecker';
import { TwoFactorVerification } from '../../components/auth/TwoFactorVerification';
import { setTwoFactorSession, get2faPendingFlagRaw, clear2faPendingFlag } from '../../lib/hooks/useTwoFactor';
import { tryTrustedDeviceLogin } from '../../lib/twofa/twofaApi';
import { useGoogleOneTap } from '../../lib/hooks/useGoogleOneTap';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';
import { isRegistrationPending, getRegistrationResumeUrl } from '../../lib/services/registrationResume';

// Componente interno che utilizza reCAPTCHA
function LoginForm() {
  const [searchParams] = useSearchParams();
  const recoveryMode = searchParams.get('recovery') === '1';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Login a due step (email -> password/SSO)
  const [loginStep, setLoginStep] = useState<'email' | 'password'>('email');
  const [availableMethods, setAvailableMethods] = useState<{ password: boolean; microsoft: boolean; google: boolean } | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // FLAG TEMPORANEO: login two-step + SSO Microsoft attivo solo in ambiente di sviluppo (npm run dev).
  // In produzione (build deployata su Plesk) resta il form singolo email+password di sempre, finché
  // la feature non viene validata e abilitata per il rollout. Vedi MD/integrations/microsoft-sso.md.
  // Per abilitarla in produzione: rimuovere questo flag e il branch "else" nel render sotto.
  // Go-live 16 lug 2026: two-step + SSO Google/Microsoft attivi anche in
  // produzione (gating residuo solo via env VITE_OAUTH_*). Il form legacy
  // single-step sotto resta come ramo morto da rimuovere in una pulizia futura.
  const twoStepLoginEnabled = true;

  const [showResetPassword, setShowResetPassword] = useState(() => recoveryMode);

  const [resetEmail, setResetEmail] = useState('');
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  // Stati per il nuovo flusso OTP
  const [resetStep, setResetStep] = useState<'email' | 'code' | 'password'>('email');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [canResendCode, setCanResendCode] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [attemptsLeft, setAttemptsLeft] = useState(3);

  // 2FA states
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorEmail, setTwoFactorEmail] = useState('');
  const [twoFactorMethods, setTwoFactorMethods] = useState<('totp' | 'email')[]>([]);
  const [twoFactorVerifying, setTwoFactorVerifying] = useState(false); // Loading state after 2FA success
  const [isTransitioning, setIsTransitioning] = useState(false); // Animation state for form transition
  const [isCheckingAuth, setIsCheckingAuth] = useState(true); // Initial auth check
  const [oauthLoading, setOauthLoading] = useState<string | null>(null); // Track which OAuth provider is loading
  // Sessione con registrazione a metà rilevata: la login resta visibile e
  // mostriamo un banner con il link per riprendere il wizard (niente più
  // redirect automatico: l'utente potrebbe voler accedere con un ALTRO account).
  const [pendingRegistration, setPendingRegistration] = useState<{ email: string; resumeUrl: string | null } | null>(null);

  const { signIn, signInWithOAuth, clearTwoFactorPending, completeTwoFactorVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useApp();

  // Get the redirect URL from the location state (set by ProtectedRoute)
  const from = location.state?.from;
  const redirectUrl = from ? `${from.pathname}${from.search || ''}` : null;
  // Recovery return is restricted to this same-origin account-deletion route.
  const recoveryReturnUrl = from?.pathname === '/account-deletion' && (!from.search || from.search === '') ? from.pathname : '/account-deletion';
  useEffect(() => {
    setIsCheckingAuth(true);
    setShowResetPassword(recoveryMode);
    setResetStep('email'); setResetEmail(''); setOtpCode(['', '', '', '', '', '']);
    setResetToken(''); setNewPassword(''); setConfirmNewPassword(''); setError(null);
  }, [recoveryMode]);
  const leavePasswordRecovery = () => {
    if (loading) return;
    setShowResetPassword(false); setResetStep('email'); setResetEmail('');
    setOtpCode(['', '', '', '', '', '']); setResetToken(''); setNewPassword(''); setConfirmNewPassword(''); setError(null);
    if (recoveryMode) navigate(recoveryReturnUrl, { replace: true });
  };


  // Google One Tap — stesso rollout gate del resto delle superfici Google (vedi MD/integrations/google-sso.md).
  // Spento col flusso reset password aperto (il prompt si sovrapporrebbe al form OTP).
  useGoogleOneTap({
    enabled: import.meta.env.VITE_OAUTH_GOOGLE_ENABLED === 'true' && !recoveryMode && !showResetPassword,
    onError: setError,
    returnTo: redirectUrl,
  });
  
  // Non applichiamo più la classe auth-page per rimuovere l'animazione dello sfondo
  // Lo sfondo rimane quello statico del CRM

  // Ottieni la chiave del sito reCAPTCHA dalle variabili d'ambiente
  const recaptchaSiteKey = import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY || 
                          '6LdR1x0rAAAAAEaN_aDN9JSeAg7tYFssz_S_6ag7'; // Fallback alla chiave fornita
  
  // Check if user is already logged in
  useEffect(() => {
    let authCheckCancelled = false;
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (authCheckCancelled) return;
        if (session) {
          // 2FA ancora pendente (browser chiuso/ricaricato a metà verifica):
          // ripristino soft dello step OTP invece di ripartire dal form email.
          // La sessione resta "sospesa" (ProtectedRoute blocca via twoFactorPending
          // in auth.ts) finché la verifica non completa.
          const pendingFlag = get2faPendingFlagRaw();
          if (pendingFlag && pendingFlag.userId === session.user.id) {
            if (pendingFlag.expired) {
              // Verifica 2FA abbandonata oltre il TTL: fail-closed, la sessione
              // non deve sopravvivere (stesso comportamento di auth.ts).
              clear2faPendingFlag();
              clearTwoFactorPending();
              await supabase.auth.signOut({ scope: 'local' });
              if (authCheckCancelled) return;
              setIsCheckingAuth(false);
              return;
            }
            try {
              const { data: twoFaStatus, error: twoFaError } = await supabase.functions.invoke('twofa-get-status', {
                headers: { Authorization: `Bearer ${session.access_token}` }
              });
              if (authCheckCancelled) return;

              if (!twoFaError && twoFaStatus?.required) {
                if (!twoFaStatus.setupCompleted) {
                  navigate('/2fa-setup', { state: { redirectTo: redirectUrl } });
                  return;
                }
                setTwoFactorEmail(session.user.email ?? '');
                setTwoFactorMethods(twoFaStatus.enabledMethods || twoFaStatus.availableMethods || ['totp', 'email']);
                setShowTwoFactor(true);
                setIsCheckingAuth(false);
                return;
              }

              if (!twoFaError && twoFaStatus && !twoFaStatus.required) {
                // 2FA non più richiesto: pulisci il pending e prosegui col
                // normale flusso utente-già-loggato qui sotto.
                clearTwoFactorPending();
              } else {
                throw new Error(twoFaError?.message || 'Stato 2FA non verificabile');
              }
            } catch (restoreError) {
              if (authCheckCancelled) return;
              // Fail-closed: senza stato 2FA verificabile la sessione sospesa
              // non può né entrare né mostrare l'OTP → logout e login da capo.
              console.error('[Login] Ripristino step 2FA fallito, signOut fail-closed:', restoreError);
              clearTwoFactorPending();
              await supabase.auth.signOut({ scope: 'local' });
              if (authCheckCancelled) return;
              setIsCheckingAuth(false);
              return;
            }
          }
          // Sessione con registrazione a metà (zero ruoli): NIENTE redirect
          // automatico al wizard — la login resta la login (l'utente potrebbe
          // aver iniziato per sbaglio una registrazione con un altro account,
          // es. OAuth col Gmail personale, e voler accedere con quello giusto).
          // Mostriamo il form con un banner "Riprendi la registrazione".
          // Il vecchio loop infinito '/'→'/login' non si ripresenta: nasceva
          // dal navigate('/') qui sotto, che per questi utenti non scatta più.
          const registrationPending = await isRegistrationPending(session.user.id);
          if (authCheckCancelled) return;
          if (registrationPending) {
            // Il resume URL va calcolato PRIMA di ogni signOut: la edge
            // register-oauth-start richiede la sessione autenticata.
            const resumeUrl = await getRegistrationResumeUrl();
            if (authCheckCancelled) return;
            setPendingRegistration({ email: session.user.email ?? '', resumeUrl });
            setIsCheckingAuth(false);
            return;
          }
          if (authCheckCancelled) return;
          // Only the final already-authenticated redirect is skipped. Pending 2FA/registration above stays authoritative.
          if (recoveryMode) {
            const { data: current, error: sessionError } = await supabase.auth.getSession();
            if (authCheckCancelled) return;
            if (sessionError || current.session?.user.id !== session.user.id) {
              setError('La sessione è cambiata. Riapri il recupero password.');
              setIsCheckingAuth(false); return;
            }
            if (current.session.user.email_confirmed_at && current.session.user.email) setResetEmail(current.session.user.email);
            setIsCheckingAuth(false); return;
          }
          // User is already logged in, redirect
          navigate(redirectUrl || '/');
          return;
        }
      } catch (error) {
        if (authCheckCancelled) return;
        console.error('Error checking auth:', error);
      }
      if (authCheckCancelled) return;
      setIsCheckingAuth(false);
    };

    checkAuth();
    return () => { authCheckCancelled = true; };
  }, [navigate, redirectUrl, recoveryMode]);

  // Pre-riempi email dal parametro URL
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [searchParams]);

  // Errore di ritorno da un flusso SSO (es. consenso Microsoft rifiutato)
  useEffect(() => {
    const ssoError = searchParams.get('sso_error');
    if (ssoError) {
      setError(decodeURIComponent(ssoError));
    }
  }, [searchParams]);

  // Inizializza reCAPTCHA
  useEffect(() => {
    const loadRecaptcha = async () => {
      try {
        await initRecaptcha(recaptchaSiteKey);
        setRecaptchaReady(true);
      } catch (error) {
        console.error('Failed to initialize reCAPTCHA:', error);
        // In development, we'll still consider it ready for testing
        if (isDevEnvironment()) {
          setRecaptchaReady(true);
        }
      }
    };

    loadRecaptcha();
  }, [recaptchaSiteKey]);

  // Countdown per reinvio codice
  useEffect(() => {
    if (resetStep === 'code' && !canResendCode && resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(prev => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (resendCountdown === 0) {
      setCanResendCode(true);
    }
  }, [resetStep, canResendCode, resendCountdown]);

  // Dopo la password, se serve la 2FA: un dispositivo ricordato (30 giorni) salta il codice,
  // altrimenti si apre il passo del codice
  const openTwoFactorStep = async (methods: ('totp' | 'email')[]) => {
    const trustedSessionToken = await tryTrustedDeviceLogin();
    if (trustedSessionToken) {
      console.log('[Login] Dispositivo ricordato: verifica in due passaggi superata senza codice');
      await handleTwoFactorVerify(trustedSessionToken);
      return;
    }
    console.log('[Login] 2FA verification required, showing modal...');
    setTwoFactorEmail(email);
    setTwoFactorMethods(methods);
    // Start transition animation
    setIsTransitioning(true);
    setTimeout(() => {
      setShowTwoFactor(true);
      setIsTransitioning(false);
    }, 300);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Bypass reCAPTCHA in ambiente di sviluppo
      if (isDevEnvironment() || !recaptchaReady) {
        console.log('Ambiente di sviluppo o reCAPTCHA non pronto. Bypassing reCAPTCHA...');
        const result = await signIn(email, password);
        if (result.error) throw result.error;

        // Check for 2FA requirements
        if (result.requiresTwoFactorSetup) {
          console.log('[Login] 2FA setup required, redirecting...');
          if (recaptchaReady) {
            unloadRecaptcha();
          }
          navigate('/2fa-setup', { state: { redirectTo: redirectUrl } });
          return;
        }

        if (result.requiresTwoFactorVerification && result.twoFactorState) {
          await openTwoFactorStep(result.twoFactorState.availableMethods);
          return;
        }

        // Clear localStorage to ensure fresh state
        localStorage.removeItem('settings-storage');

        // Rimuovi reCAPTCHA dalla pagina dopo il login (anche in dev per sicurezza)
        if (recaptchaReady) {
          console.log('[Login] Rimozione reCAPTCHA dopo login riuscito (dev)');
          unloadRecaptcha();
        }

        // Get the settings to check for default_route
        try {
          const { data: userProfile } = await supabase
            .from('user_profiles')
            .select('organization_id, is_super_admin, is_partner_admin')
            .eq('id', (await supabase.auth.getUser()).data.user?.id)
            .single();

          // Dual-role user: imposta CRM mode di default (switch ad admin dal dropdown utente)
          if (userProfile?.organization_id && (userProfile?.is_super_admin || userProfile?.is_partner_admin)) {
            console.log("[Login] Dual-role user detected, defaulting to CRM mode");
            localStorage.setItem('flusia_active_role', 'crm');
          }

          if (userProfile?.organization_id) {
            // Get settings for this organization
            const { data: settingsData } = await supabase
              .from('settings')
              .select('default_route')
              .eq('organization_id', userProfile.organization_id)
              .single();

            if (settingsData?.default_route) {
              console.log("[Login] Redirecting to default route:", settingsData.default_route);

              // Initialize session checker after successful login
              try {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                  console.log('[Login] Initializing session checker after successful login');
                  await sessionChecker.initializeAfterLogin(user.id);
                }
              } catch (sessionError) {
                console.error('[Login] Error initializing session checker:', sessionError);
                // Non bloccare l'app se c'è un errore con le sessioni
              }

              // Priority: redirectUrl (from ProtectedRoute) > default_route > '/'
              if (redirectUrl) {
                console.log("[Login] Redirecting to original URL:", redirectUrl);
                navigate(redirectUrl, { replace: true });
              } else {
                console.log("[Login] Redirecting to default route:", settingsData.default_route);
                navigate(settingsData.default_route);
              }
              return;
            }
          }
        } catch (settingsError) {
          console.error("Error getting default route:", settingsError);
        }

        // Initialize session checker after successful login
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            console.log('[Login] Initializing session checker after successful login');
            await sessionChecker.initializeAfterLogin(user.id);
          }
        } catch (sessionError) {
          console.error('[Login] Error initializing session checker:', sessionError);
          // Non bloccare l'app se c'è un errore con le sessioni
        }

        // If no default route found, go to redirectUrl or root
        if (redirectUrl) {
          console.log("[Login] Redirecting to original URL:", redirectUrl);
          navigate(redirectUrl, { replace: true });
        } else {
          navigate('/');
        }
        return;
      }
      
      // Esegui reCAPTCHA e ottieni un token
      const recaptchaToken = await executeRecaptcha('login');
      
      if (!recaptchaToken) {
        throw new Error('Errore nell\'esecuzione di reCAPTCHA. Riprova più tardi.');
      }
      
      // Verifica il token reCAPTCHA
      const recaptchaResult = await verifyRecaptcha(recaptchaToken, 'login');
      
      if (!recaptchaResult.success) {
        throw new Error(recaptchaResult.message || 'Verifica di sicurezza fallita. Riprova.');
      }
      
      // Se reCAPTCHA è verificato con successo, procedi con il login
      const result = await signIn(email, password);
      if (result.error) throw result.error;

      // Check for 2FA requirements
      if (result.requiresTwoFactorSetup) {
        console.log('[Login] 2FA setup required, redirecting...');
        if (recaptchaReady) {
          unloadRecaptcha();
        }
        navigate('/2fa-setup', { state: { redirectTo: redirectUrl } });
        return;
      }

      if (result.requiresTwoFactorVerification && result.twoFactorState) {
        await openTwoFactorStep(result.twoFactorState.availableMethods);
        return;
      }

      // Clear localStorage to ensure fresh state
      localStorage.removeItem('settings-storage');

      // Initialize session checker after successful login
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          console.log('[Login] Initializing session checker after successful login');
          await sessionChecker.initializeAfterLogin(user.id);
        }
      } catch (sessionError) {
        console.error('[Login] Error initializing session checker:', sessionError);
        // Non bloccare l'app se c'è un errore con le sessioni
      }

      // Rimuovi reCAPTCHA dalla pagina dopo il login
      if (recaptchaReady && !isDevEnvironment()) {
        console.log('[Login] Rimozione reCAPTCHA dopo login riuscito');
        unloadRecaptcha();
      }

      // Get the settings to check for default_route
      try {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('organization_id, is_super_admin, is_partner_admin')
          .eq('id', (await supabase.auth.getUser()).data.user?.id)
          .single();

        // Dual-role user: imposta CRM mode di default
        if (userProfile?.organization_id && (userProfile?.is_super_admin || userProfile?.is_partner_admin)) {
          console.log("[Login] Dual-role user detected (post-2FA), defaulting to CRM mode");
          localStorage.setItem('flusia_active_role', 'crm');
        }

        if (userProfile?.organization_id) {
          // Get settings for this organization
          const { data: settingsData } = await supabase
            .from('settings')
            .select('default_route')
            .eq('organization_id', userProfile.organization_id)
            .single();

          if (settingsData?.default_route) {
            // Priority: redirectUrl (from ProtectedRoute) > default_route > '/'
            if (redirectUrl) {
              console.log("[Login] Redirecting to original URL:", redirectUrl);
              navigate(redirectUrl, { replace: true });
            } else {
              console.log("[Login] Redirecting to default route:", settingsData.default_route);
              navigate(settingsData.default_route);
            }
            return;
          }
        }
      } catch (settingsError) {
        console.error("Error getting default route:", settingsError);
      }

      // If no default route found, go to redirectUrl or root
      if (redirectUrl) {
        console.log("[Login] Redirecting to original URL:", redirectUrl);
        navigate(redirectUrl, { replace: true });
      } else {
        navigate('/');
      }
    } catch (error: any) {
      // Gestione dell'errore migliorata
      console.error('Login error:', error);
      
      // Determina se è un errore di reCAPTCHA o di autenticazione
      if (error.message?.includes('reCAPTCHA') || error.message?.includes('sicurezza')) {
        setError(`Errore di verifica: ${error.message}`);
      } else {
        setError(error.message || 'Accesso fallito. Verifica email e password.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Step email del login two-step: verifica reCAPTCHA e chiede alla edge quali metodi mostrare
  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCheckingEmail(true);

    try {
      if (!isDevEnvironment() && recaptchaReady) {
        const recaptchaToken = await executeRecaptcha('check_login_methods');
        if (!recaptchaToken) {
          throw new Error('Errore nell\'esecuzione di reCAPTCHA. Riprova più tardi.');
        }
        const recaptchaResult = await verifyRecaptcha(recaptchaToken, 'check_login_methods');
        if (!recaptchaResult.success) {
          throw new Error(recaptchaResult.message || 'Verifica di sicurezza fallita. Riprova.');
        }
      }

      // Fallback resiliente: se la edge non risponde, si procede comunque allo step password
      let methods = { password: true, microsoft: false, google: false };
      try {
        const { data, error: invokeError } = await supabase.functions.invoke('check-login-methods', {
          body: { email }
        });

        if (data?.error && data?.retryAfter) {
          setError(`Troppe richieste. Riprova tra ${data.retryAfter} secondi.`);
          return;
        }

        if (!invokeError && data && typeof data.password === 'boolean') {
          methods = { password: data.password, microsoft: !!data.microsoft, google: !!data.google };
        }
      } catch (checkError) {
        console.error('[Login] check-login-methods fallita, fallback a password:', checkError);
      }

      setAvailableMethods(methods);
      setIsTransitioning(true);
      setTimeout(() => {
        setLoginStep('password');
        setIsTransitioning(false);
      }, 300);
    } catch (error: any) {
      console.error('Email continue error:', error);
      if (error.message?.includes('reCAPTCHA') || error.message?.includes('sicurezza')) {
        setError(`Errore di verifica: ${error.message}`);
      } else {
        setError(error.message || 'Errore. Riprova.');
      }
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleBackToEmail = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setLoginStep('email');
      setPassword('');
      setError(null);
      setAvailableMethods(null);
      setIsTransitioning(false);
    }, 300);
  };

  // Step 1: Invia codice OTP via email
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      setError('Inserisci un indirizzo email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Bypass reCAPTCHA in ambiente di sviluppo
      if (isDevEnvironment() || !recaptchaReady) {
        console.log('Ambiente di sviluppo o reCAPTCHA non pronto. Bypassing reCAPTCHA...');
        const { data, error } = await supabase.functions.invoke('send-password-reset-code', {
          body: { email: resetEmail }
        });

        if (error) throw error;

        if (data?.error) {
          if (data.retryAfter) {
            setError(`Attendi ${data.retryAfter} secondi prima di richiedere un nuovo codice`);
          } else {
            throw new Error(data.error);
          }
          return;
        }

        toast.success('Codice inviato via email!');
        setResetStep('code');
        setCanResendCode(false);
        setResendCountdown(60);
        return;
      }

      // Esegui reCAPTCHA e ottieni un token
      const recaptchaToken = await executeRecaptcha('reset_password_request');

      if (!recaptchaToken) {
        throw new Error('Errore nell\'esecuzione di reCAPTCHA. Riprova più tardi.');
      }

      // Verifica il token reCAPTCHA
      const recaptchaResult = await verifyRecaptcha(recaptchaToken, 'reset_password_request');

      if (!recaptchaResult.success) {
        throw new Error(recaptchaResult.message || 'Verifica di sicurezza fallita. Riprova.');
      }

      // Se reCAPTCHA è verificato con successo, procedi con l'invio del codice
      const { data, error } = await supabase.functions.invoke('send-password-reset-code', {
        body: { email: resetEmail }
      });

      if (error) throw error;

      if (data?.error) {
        if (data.retryAfter) {
          setError(`Attendi ${data.retryAfter} secondi prima di richiedere un nuovo codice`);
        } else {
          throw new Error(data.error);
        }
        return;
      }

      toast.success('Codice inviato via email!');
      setResetStep('code');
      setCanResendCode(false);
      setResendCountdown(60);
    } catch (error: any) {
      console.error('Send code error:', error);

      // Determina se è un errore di reCAPTCHA o di invio codice
      if (error.message?.includes('reCAPTCHA') || error.message?.includes('sicurezza')) {
        setError(`Errore di verifica: ${error.message}`);
      } else {
        setError(error.message || 'Errore nell\'invio del codice');
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verifica codice OTP
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = otpCode.join('');

    if (code.length !== 6) {
      setError('Inserisci il codice completo di 6 cifre');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('verify-password-reset-code', {
        body: {
          email: resetEmail,
          code: code
        }
      });

      // Controlla prima se data contiene un errore strutturato (anche con status non-2xx)
      if (data?.error) {
        if (data.tooManyAttempts) {
          setError('Troppi tentativi falliti. Richiedi un nuovo codice.');
          setResetStep('email');
          setOtpCode(['', '', '', '', '', '']);
        } else if (data.attemptsLeft !== undefined) {
          setAttemptsLeft(data.attemptsLeft);
          setError(`Codice non valido. Tentativi rimasti: ${data.attemptsLeft}`);
        } else {
          setError(data.error);
        }
        return;
      }

      // Se c'è un errore generico senza data strutturato
      if (error) throw error;

      if (data?.success) {
        setResetToken(data.resetToken);
        setResetStep('password');
        toast.success('Codice verificato! Imposta la nuova password.');
      }
    } catch (error: any) {
      console.error('Verify code error:', error);
      setError(error.message || 'Errore nella verifica del codice');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Imposta nuova password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmNewPassword) {
      setError('Compila tutti i campi');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('Le password non corrispondono');
      return;
    }

    if (newPassword.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('reset-password-with-code', {
        body: {
          email: resetEmail,
          resetToken: resetToken,
          newPassword: newPassword
        }
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.success) {
        toast.success('Password aggiornata con successo!');
        // Reset stati e torna al login
        setShowResetPassword(false);
        setResetStep('email');
        setResetEmail('');
        setOtpCode(['', '', '', '', '', '']);
        setResetToken('');
        setNewPassword('');
        setConfirmNewPassword('');
        if (recoveryMode) navigate(recoveryReturnUrl, { replace: true });
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      setError(error.message || 'Errore nel reset della password');
    } finally {
      setLoading(false);
    }
  };

  // Funzione per reinviare il codice
  const handleResendCode = async () => {
    setOtpCode(['', '', '', '', '', '']);
    setError(null);
    await handleSendCode(new Event('submit') as any);
  };

  // Handler per la verifica 2FA
  const handleTwoFactorVerify = async (sessionToken: string) => {
    // Show loading state immediately to prevent flash
    setTwoFactorVerifying(true);

    // Dopo il codice si RICARICA la pagina invece di navigare dentro l'app: prima del codice il
    // database rifiuta le richieste di questa sessione (controllo 2FA, 16/10/2026), quindi profilo,
    // permessi e impostazioni chiesti in quel momento sono vuoti. Navigando, l'app trovava zero
    // permessi, scambiava la sessione per una registrazione a meta' e chiamava signOut.
    const vai = (path: string) => window.location.replace(path);

    setTwoFactorSession(sessionToken, new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString());
    completeTwoFactorVerification();

    // Rimuovi reCAPTCHA
    if (recaptchaReady) {
      unloadRecaptcha();
    }

    // Initialize session checker
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await sessionChecker.initializeAfterLogin(user.id);
      }
    } catch (sessionError) {
      console.error('[Login] Error initializing session checker:', sessionError);
    }

    // Navigate to appropriate route
    // Priority: redirectUrl (from ProtectedRoute) > default_route > '/'
    if (redirectUrl) {
      console.log("[Login] 2FA complete, redirecting to original URL:", redirectUrl);
      vai(redirectUrl);
      return;
    }

    try {
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id, is_super_admin, is_partner_admin')
        .eq('id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      // Dual-role user: imposta CRM mode di default
      if (userProfile?.organization_id && (userProfile?.is_super_admin || userProfile?.is_partner_admin)) {
        console.log("[Login] Dual-role user detected (post-2FA-verify), defaulting to CRM mode");
        localStorage.setItem('flusia_active_role', 'crm');
      }

      if (userProfile?.organization_id) {
        const { data: settingsData } = await supabase
          .from('settings')
          .select('default_route')
          .eq('organization_id', userProfile.organization_id)
          .single();

        if (settingsData?.default_route) {
          vai(settingsData.default_route);
          return;
        }
      }
    } catch (settingsError) {
      console.error("Error getting default route:", settingsError);
    }

    vai('/');
  };

  const handleTwoFactorCancel = async () => {
    setShowTwoFactor(false);
    setTwoFactorEmail('');
    setTwoFactorMethods([]);
    clearTwoFactorPending();
    // Sign out since we're canceling 2FA
    await supabase.auth.signOut({ scope: 'local' });
  };

  // Show loading state during initial auth check
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center gap-2 text-[#787774]">
          <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
          <span>Caricamento...</span>
        </div>
      </div>
    );
  }

  // Show loading state after successful 2FA verification (prevents flash)
  if (twoFactorVerifying) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center gap-2 text-[#787774]">
          <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
          <span>Accesso in corso...</span>
        </div>
      </div>
    );
  }

  // Combined view for login and 2FA with smooth transitions
  // This is handled in the main return below

  if (showResetPassword && !showTwoFactor && !pendingRegistration) {
    const getTitleAndSubtitle = () => {
      switch (resetStep) {
        case 'email':
          return {
            title: 'Recupera password',
            subtitle: 'Inserisci il tuo indirizzo email'
          };
        case 'code':
          return {
            title: 'Verifica codice',
            subtitle: 'Inserisci il codice ricevuto via email'
          };
        case 'password':
          return {
            title: 'Nuova password',
            subtitle: 'Imposta la tua nuova password'
          };
      }
    };

    const { title, subtitle } = getTitleAndSubtitle();

    return (
      <RegisterSplitLayout visual={{ startCard: 'quote' }}>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#37352F] mb-1">
            {title}
          </h1>
          <p className="text-sm text-[#787774]">
            {subtitle}
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
            {error}
          </div>
        )}

        {/* Step 1: Email */}
        {resetStep === 'email' && (
          <form className="space-y-5" onSubmit={handleSendCode}>
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-[#37352F] mb-1.5">
                Indirizzo email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-[#787774]" />
                </div>
                <input
                  id="reset-email"
                  name="reset-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 bg-white border border-[rgba(55,53,47,0.16)] rounded-lg leading-5 text-sm focus:outline-none focus:border-[#2383E2]"
                  placeholder="tu@azienda.com"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={leavePasswordRecovery}
                disabled={loading}
                className="flex items-center text-sm text-[#2383E2] hover:underline"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {recoveryMode ? 'Torna alla cancellazione account' : 'Torna al login'}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading || (!isDevEnvironment() && !recaptchaReady)}
              className="notion-register-btn w-full"
            >
              {loading ? 'Invio in corso...' : 'Invia codice'}
            </button>
          </form>
        )}

        {/* Step 2: Codice OTP */}
        {resetStep === 'code' && (
          <form className="space-y-5" onSubmit={handleVerifyCode}>
            <div>
              <label className="block text-sm font-medium text-[#37352F] mb-3">
                Codice di verifica (6 cifre)
              </label>
              <div className="flex gap-2 justify-center">
                {otpCode.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value.length <= 1) {
                        const newCode = [...otpCode];
                        newCode[index] = value;
                        setOtpCode(newCode);

                        // Auto-focus al prossimo input
                        if (value && index < 5) {
                          const nextInput = e.target.parentElement?.children[index + 1] as HTMLInputElement;
                          nextInput?.focus();
                        }
                      }
                    }}
                    onKeyDown={(e) => {
                      // Backspace: vai al campo precedente
                      if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
                        const prevInput = e.currentTarget.parentElement?.children[index - 1] as HTMLInputElement;
                        prevInput?.focus();
                      }
                    }}
                    className="w-12 h-14 text-center text-2xl font-bold bg-white border border-[rgba(55,53,47,0.16)] rounded-lg focus:outline-none focus:border-[#2383E2]"
                  />
                ))}
              </div>
              <p className="mt-3 text-xs text-center text-[#787774]">
                Tentativi rimasti: {attemptsLeft}
              </p>
            </div>

            <div className="space-y-3">
              {!canResendCode ? (
                <p className="text-sm text-center text-[#787774]">
                  Richiedi nuovo codice tra {resendCountdown}s
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading}
                  className="w-full text-sm text-[#2383E2] hover:underline disabled:opacity-50"
                >
                  Reinvia codice
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setResetStep('email');
                  setOtpCode(['', '', '', '', '', '']);
                  setError(null);
                }}
                className="flex items-center mx-auto text-sm text-[#2383E2] hover:underline"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Cambia email
              </button>
            </div>

            <button
              type="submit"
              disabled={loading || otpCode.join('').length !== 6}
              className="notion-register-btn w-full"
            >
              {loading ? 'Verifica in corso...' : 'Verifica codice'}
            </button>
          </form>
        )}

        {/* Step 3: Nuova Password */}
        {resetStep === 'password' && (
          <form className="space-y-5" onSubmit={handleResetPassword}>
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-[#37352F] mb-1.5">
                Nuova password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-[#787774]" />
                </div>
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 bg-white border border-[rgba(55,53,47,0.16)] rounded-lg leading-5 text-sm focus:outline-none focus:border-[#2383E2]"
                  placeholder="Minimo 6 caratteri"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirm-new-password" className="block text-sm font-medium text-[#37352F] mb-1.5">
                Conferma password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-[#787774]" />
                </div>
                <input
                  id="confirm-new-password"
                  name="confirm-new-password"
                  type="password"
                  required
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 bg-white border border-[rgba(55,53,47,0.16)] rounded-lg leading-5 text-sm focus:outline-none focus:border-[#2383E2]"
                  placeholder="Ripeti la password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="notion-register-btn w-full"
            >
              {loading ? 'Aggiornamento...' : 'Aggiorna password'}
            </button>
          </form>
        )}

        {recoveryMode && resetStep !== 'email' && <button type="button" disabled={loading} onClick={leavePasswordRecovery} className="mt-5 text-sm text-[#2383E2] hover:underline disabled:opacity-50">Torna alla cancellazione account</button>}

        <p className="mt-8 text-xs text-[#787774] text-center">
          <a
            href="https://obly.it"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#37352F] transition-colors"
          >
            Crafted with ❤️ by Obly
          </a>
        </p>
      </RegisterSplitLayout>
    );
  }

  // Aggiungiamo una piccola nota informativa sulla sicurezza
  const renderSecurityNote = () => (
    <div className="mt-4 text-xs text-center text-[#787774]">
      <span>Questo sito è protetto da reCAPTCHA per garantire la sicurezza dell'accesso</span>
    </div>
  );

  return (
    <RegisterSplitLayout visual={{ startCard: 'quote' }}>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#37352F] mb-1">Accedi</h1>
        <p className="text-sm text-[#787774]">Bentornato su Flusia</p>
      </div>

      {/* Forms container with transitions */}
      <div className="relative">
        {/* Login Form */}
        <div
          className={`transition-all duration-300 ease-out ${
            showTwoFactor
              ? 'opacity-0 -translate-y-8 absolute inset-x-0 pointer-events-none'
              : isTransitioning
                ? 'opacity-0 -translate-y-8'
                : 'opacity-100 translate-y-0'
          }`}
        >
          {pendingRegistration && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              C'è una registrazione non completata per{' '}
              <span className="font-semibold">{pendingRegistration.email}</span>.{' '}
              {pendingRegistration.resumeUrl ? (
                <>
                  <a href={pendingRegistration.resumeUrl} className="underline font-medium">
                    Riprendila da dove eri rimasto
                  </a>{' '}
                  oppure accedi qui sotto con un altro account.
                </>
              ) : (
                <>Accedi qui sotto, oppure ripartendo da <a href="/register" className="underline font-medium">flusia.it/register</a>.</>
              )}
            </div>
          )}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
              {error}
            </div>
          )}
          {twoStepLoginEnabled ? (
            loginStep === 'email' ? (
            <>
              <form className="space-y-5" onSubmit={handleEmailContinue}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#37352F] mb-1.5">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-[#787774]" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2.5 bg-white border border-[rgba(55,53,47,0.16)] rounded-lg leading-5 text-sm focus:outline-none focus:border-[#2383E2]"
                      placeholder="tu@azienda.com"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={checkingEmail || (!isDevEnvironment() && !recaptchaReady)}
                  className="notion-register-btn w-full"
                >
                  {checkingEmail ? 'Verifica in corso...' : 'Avanti'}
                </button>
              </form>

              {/* Niente bottoni social nello step email: i metodi di accesso (password e/o
                  Google/Microsoft) compaiono nello step successivo IN BASE all'email inserita
                  (decisione UX 15 lug 2026 — pattern HubSpot puro). Il One Tap resta attivo. */}

              {renderSecurityNote()}
            </>
          ) : (
            <>
              <div className="mb-5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-[#37352F] min-w-0">
                  <Mail className="h-4 w-4 text-[#787774] flex-shrink-0" />
                  <span className="truncate">{email}</span>
                </div>
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="flex items-center flex-shrink-0 text-sm text-[#2383E2] hover:underline"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Modifica
                </button>
              </div>

              {availableMethods?.password && (
                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-[#37352F] mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-[#787774]" />
                      </div>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        autoFocus
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full pl-10 pr-10 py-2.5 bg-white border border-[rgba(55,53,47,0.16)] rounded-lg leading-5 text-sm focus:outline-none focus:border-[#2383E2]"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#787774] hover:text-[#37352F] transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || (!isDevEnvironment() && !recaptchaReady)}
                    className="notion-register-btn w-full"
                  >
                    {loading ? 'Accesso in corso...' : 'Accedi'}
                  </button>
                </form>
              )}

              <div className="flex justify-end text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setResetEmail(email);
                    setShowResetPassword(true);
                  }}
                  className="text-[#2383E2] hover:underline"
                >
                  Password dimenticata?
                </button>
              </div>

              {(() => {
                const showMicrosoft = !!availableMethods?.microsoft && import.meta.env.VITE_OAUTH_MICROSOFT_ENABLED === 'true';
                const showGoogle = !!availableMethods?.google && import.meta.env.VITE_OAUTH_GOOGLE_ENABLED === 'true';
                const socialOnly = !availableMethods?.password;
                if (!showMicrosoft && !showGoogle) {
                  // Anti dead-end: account solo-social ma provider disabilitati da env
                  // (stato di misconfigurazione) — messaggio informativo, nessuna CTA
                  // password al login (la gestione password vive in Impostazioni → Sicurezza).
                  if (socialOnly) {
                    return (
                      <p className="mt-4 text-xs text-center text-[#787774]">
                        Questo account accede con un provider esterno al momento non disponibile.
                        Contatta l'amministratore.
                      </p>
                    );
                  }
                  return null;
                }
                return (
                  <>
                    {availableMethods?.password && (
                      <div className="relative my-5">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-[rgba(55,53,47,0.12)]" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-3 text-[#787774] bg-white">oppure</span>
                        </div>
                      </div>
                    )}
                    <div className="space-y-3">
                      {showGoogle && (
                        <button
                          type="button"
                          disabled={!!oauthLoading || loading}
                          onClick={async () => {
                            setOauthLoading('google');
                            setError(null);
                            const result = await signInWithOAuth('google', { loginHint: email, returnTo: redirectUrl });
                            if (result.error) {
                              setError(result.error.message || 'Errore con Google. Riprova.');
                              setOauthLoading(null);
                            }
                          }}
                          className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white hover:bg-[rgba(55,53,47,0.03)] text-[#37352F] text-sm font-medium transition-all duration-200 disabled:opacity-50"
                        >
                          {oauthLoading === 'google' ? (
                            <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
                          ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                          )}
                          Continua con Google
                        </button>
                      )}
                      {showMicrosoft && (
                        <button
                          type="button"
                          disabled={!!oauthLoading || loading}
                          onClick={async () => {
                            setOauthLoading('azure');
                            setError(null);
                            const result = await signInWithOAuth('azure', { loginHint: email, returnTo: redirectUrl });
                            if (result.error) {
                              setError(result.error.message || 'Errore con Microsoft. Riprova.');
                              setOauthLoading(null);
                            }
                          }}
                          className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white hover:bg-[rgba(55,53,47,0.03)] text-[#37352F] text-sm font-medium transition-all duration-200 disabled:opacity-50"
                        >
                          {oauthLoading === 'azure' ? (
                            <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
                          ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                              <path fill="#F25022" d="M1 1h10v10H1z"/>
                              <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                              <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                              <path fill="#FFB900" d="M13 13h10v10H13z"/>
                            </svg>
                          )}
                          Continua con Microsoft
                        </button>
                      )}
                    </div>
                    {/* Account solo-social: nessuna CTA "imposta password" al login
                        (decisione UX 15 lug 2026: la gestione password vive SOLO in
                        Impostazioni → Sicurezza); solo testo informativo. */}
                    {socialOnly && (
                      <p className="mt-4 text-xs text-center text-[#787774]">
                        {showGoogle && showMicrosoft
                          ? 'Questo account accede con Google o Microsoft.'
                          : showGoogle
                            ? 'Questo account accede con Google.'
                            : 'Questo account accede con Microsoft.'}
                      </p>
                    )}
                  </>
                );
              })()}

              {renderSecurityNote()}
            </>
            )
          ) : (
            <>
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#37352F] mb-1.5">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-[#787774]" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2.5 bg-white border border-[rgba(55,53,47,0.16)] rounded-lg leading-5 text-sm focus:outline-none focus:border-[#2383E2]"
                      placeholder="tu@azienda.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-[#37352F] mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 text-[#787774]" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full pl-10 pr-10 py-2.5 bg-white border border-[rgba(55,53,47,0.16)] rounded-lg leading-5 text-sm focus:outline-none focus:border-[#2383E2]"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#787774] hover:text-[#37352F] transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end text-sm">
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(true)}
                    className="text-[#2383E2] hover:underline"
                  >
                    Password dimenticata?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading || (!isDevEnvironment() && !recaptchaReady)}
                  className="notion-register-btn w-full"
                >
                  {loading ? 'Accesso in corso...' : 'Accedi'}
                </button>
              </form>

              {/* OAuth Social Login Buttons - only show if at least one provider is enabled */}
              {(import.meta.env.VITE_OAUTH_GOOGLE_ENABLED === 'true' ||
                import.meta.env.VITE_OAUTH_APPLE_ENABLED === 'true' ||
                import.meta.env.VITE_OAUTH_MICROSOFT_ENABLED === 'true') && (
                <>
                  {/* Divider */}
                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[rgba(55,53,47,0.12)]" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-3 text-[#787774] bg-white">oppure</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {import.meta.env.VITE_OAUTH_GOOGLE_ENABLED === 'true' && (
                      <button
                        type="button"
                        disabled={!!oauthLoading || loading}
                        onClick={async () => {
                          setOauthLoading('google');
                          setError(null);
                          const result = await signInWithOAuth('google', { returnTo: redirectUrl });
                          if (result.error) {
                            setError(result.error.message || 'Errore con Google. Riprova.');
                            setOauthLoading(null);
                          }
                        }}
                        className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white hover:bg-[rgba(55,53,47,0.03)] text-[#37352F] text-sm font-medium transition-all duration-200 disabled:opacity-50"
                      >
                        {oauthLoading === 'google' ? (
                          <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
                        ) : (
                          <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        )}
                        Continua con Google
                      </button>
                    )}

                    {import.meta.env.VITE_OAUTH_APPLE_ENABLED === 'true' && (
                      <button
                        type="button"
                        disabled={!!oauthLoading || loading}
                        onClick={async () => {
                          setOauthLoading('apple');
                          setError(null);
                          const result = await signInWithOAuth('apple', { returnTo: redirectUrl });
                          if (result.error) {
                            setError(result.error.message || 'Errore con Apple. Riprova.');
                            setOauthLoading(null);
                          }
                        }}
                        className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white hover:bg-[rgba(55,53,47,0.03)] text-[#37352F] text-sm font-medium transition-all duration-200 disabled:opacity-50"
                      >
                        {oauthLoading === 'apple' ? (
                          <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
                        ) : (
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                          </svg>
                        )}
                        Continua con Apple
                      </button>
                    )}

                    {import.meta.env.VITE_OAUTH_MICROSOFT_ENABLED === 'true' && (
                      <button
                        type="button"
                        disabled={!!oauthLoading || loading}
                        onClick={async () => {
                          setOauthLoading('azure');
                          setError(null);
                          const result = await signInWithOAuth('azure', { returnTo: redirectUrl });
                          if (result.error) {
                            setError(result.error.message || 'Errore con Microsoft. Riprova.');
                            setOauthLoading(null);
                          }
                        }}
                        className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white hover:bg-[rgba(55,53,47,0.03)] text-[#37352F] text-sm font-medium transition-all duration-200 disabled:opacity-50"
                      >
                        {oauthLoading === 'azure' ? (
                          <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
                        ) : (
                          <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#F25022" d="M1 1h10v10H1z"/>
                            <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                            <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                            <path fill="#FFB900" d="M13 13h10v10H13z"/>
                          </svg>
                        )}
                        Continua con Microsoft
                      </button>
                    )}
                  </div>
                </>
              )}

              {renderSecurityNote()}
            </>
          )}
        </div>

        {/* 2FA Form */}
        <div
          className={`transition-all duration-300 ease-out ${
            showTwoFactor
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8 absolute inset-x-0 pointer-events-none'
          }`}
        >
          {showTwoFactor && (
            <TwoFactorVerification
              email={twoFactorEmail}
              availableMethods={twoFactorMethods}
              onVerify={handleTwoFactorVerify}
              onCancel={handleTwoFactorCancel}
              embedded={true}
            />
          )}
        </div>
      </div>

      {/* Registration Link - only show on login form */}
      <div className={`mt-6 text-center transition-opacity duration-300 ${showTwoFactor ? 'opacity-0' : 'opacity-100'}`}>
        <p className="text-sm text-[#787774]">
          Non hai un account?{' '}
          <a href="/register" className="text-[#2383E2] hover:underline font-medium">
            Registrati qui
          </a>
        </p>
      </div>

      {/* Footer - Crafted by Obly */}
      <p className="mt-8 text-xs text-[#787774] text-center">
        <a
          href="https://obly.it"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[#37352F] transition-colors"
        >
          Crafted with ❤️ by Obly
        </a>
      </p>
    </RegisterSplitLayout>
  );
}

// Componente wrapper per Login
export function Login() {
  return <LoginForm />;
}